import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirestore, collection, getDocs, doc, getDoc } from "firebase/firestore";
import app from "./firebase";
import styles from "./ProductionBatchesDashboard.module.css";

const db = getFirestore(app);

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const d = toJsDate(value);
  return d ? d.toLocaleString() : "-";
};

const compareValues = (a, b) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  const da = toJsDate(a);
  const dbb = toJsDate(b);
  if (da && dbb) return da.getTime() - dbb.getTime();

  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;

  return String(a).localeCompare(String(b));
};

export default function ProductionBatchesDashboard() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [prodSnap, eventsSnap] = await Promise.all([
          getDoc(doc(db, "products", productId)),
          getDocs(collection(db, "productionEvents"))
        ]);

        setProduct(prodSnap.exists() ? { id: prodSnap.id, ...prodSnap.data() } : null);

        const all = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = all.filter(e => e.productId === productId);
        setEvents(filtered);
      } catch (err) {
        console.error("Error fetching production batches:", err);
        setProduct(null);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [productId]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = events.filter(e => {
      if (!term) return true;
      const hay = [e.batchCode, e.recipeName, e.productName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => dir * compareValues(a?.[sortField], b?.[sortField]));
  }, [events, search, sortField, sortDir]);

  if (loading) return <p className={styles.loading}>Loading batches...</p>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{product?.name || "Production Batches"}</h2>
          <div className={styles.subtitle}>Batches</div>
        </div>
        <button className={styles.backButton} onClick={() => navigate("/production-history")}>Back</button>
      </div>

      <div className={styles.controls}>
        <input
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search batch code, recipe..."
        />
        <select className={styles.select} value={sortField} onChange={(e) => setSortField(e.target.value)}>
          <option value="date">Date</option>
          <option value="batchCode">Batch Code</option>
          <option value="quantityProduced">Quantity Produced</option>
        </select>
        <select className={styles.select} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </select>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Prod Date</th>
              <th>Batch Code</th>
              <th>Quantity Produced</th>
              <th>Recipe</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map(e => (
              <tr key={e.id} className={styles.row} onClick={() => navigate(`/production-history/batch/${e.id}`)}>
                <td>{formatDateTime(e.date)}</td>
                <td>{e.batchCode || "-"}</td>
                <td>{e.quantityProduced ?? "-"}</td>
                <td>{e.recipeName || "-"}</td>
              </tr>
            ))}
            {filteredAndSorted.length === 0 && (
              <tr><td className={styles.empty} colSpan={4}>No batches found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
